import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import AlertModal from '../components/modals/AlertModal';
import CornerNotice from '../components/notifications/CornerNotice';
import { useAuth } from '../contexts/AuthContext';
import './TaxonomyManager.css';

const TYPE_OPTIONS = [
  { value: 'concentration', label: 'Concentration / pathway' },
  { value: 'subject', label: 'Subject' },
  { value: 'standard', label: 'Standard' },
  { value: 'skill', label: 'Skill' },
  { value: 'objective', label: 'Objective' },
  { value: 'tag', label: 'Tag' }
];

const EMPTY_FORM = {
  title: '',
  short_title: '',
  description: '',
  type: 'subject',
  parents: [],
  pathways: [],
  is_global: false,
  status: 'active'
};

function displayTitle(row) {
  return row?.title || row?.short_title || '—';
}

function asIdArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map(String).filter(Boolean);
}

function MultiIdSelect({ id, label, options, value, onChange, hint }) {
  const selected = new Set(asIdArray(value));
  return (
    <div className="taxonomy-field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        multiple
        size={Math.min(8, Math.max(4, options.length || 4))}
        value={asIdArray(value)}
        onChange={(e) => {
          const next = Array.from(e.target.selectedOptions).map((o) => o.value);
          onChange(next);
        }}
      >
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {displayTitle(p)} ({p.type})
            {p.organization_group_id == null ? ' · shared' : ''}
          </option>
        ))}
      </select>
      <p className="taxonomy-field-hint">
        {hint || 'Hold Ctrl/Cmd to select multiple.'}
        {selected.size ? ` Selected: ${selected.size}.` : ''}
      </p>
    </div>
  );
}

const TaxonomyManager = () => {
  const { fetchWithAuth } = useAuth();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [alertModal, setAlertModal] = useState(null);
  const [cornerNotice, setCornerNotice] = useState(null);

  const showAlert = (subject, body, variant = 'error', details = '') => {
    setAlertModal({
      variant,
      subject,
      body,
      details: details || undefined,
      actions: [{ label: 'OK', onClick: () => setAlertModal(null), variant: 'primary' }]
    });
  };

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('type', typeFilter);
      if (showInactive) params.set('include_inactive', '1');
      const res = await fetchWithAuth(`/api/taxonomy?${params.toString()}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load taxonomy');
      setRows(data.data || []);
    } catch (err) {
      showAlert('Load failed', 'Could not load taxonomy entries.', 'error', err.message);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, typeFilter, showInactive]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const title = displayTitle(r).toLowerCase();
      const shortTitle = String(r.short_title || '').toLowerCase();
      const desc = String(r.description || '').toLowerCase();
      return title.includes(q) || shortTitle.includes(q) || desc.includes(q);
    });
  }, [rows, search]);

  const refOptions = useMemo(
    () => rows.filter((r) => !editingId || String(r.id) !== String(editingId)),
    [rows, editingId]
  );

  const parentOptions = useMemo(() => {
    if (form.is_global) {
      return refOptions.filter((r) => r.organization_group_id == null);
    }
    return refOptions;
  }, [refOptions, form.is_global]);

  const pathwayOptions = useMemo(() => {
    const concentrations = parentOptions.filter((r) => r.type === 'concentration');
    return concentrations.length ? concentrations : parentOptions;
  }, [parentOptions]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, type: typeFilter || 'subject' });
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      title: row.title || '',
      short_title: row.short_title || '',
      description: row.description || '',
      type: row.type || 'subject',
      parents: asIdArray(row.parents),
      pathways: asIdArray(row.pathways),
      is_global: row.organization_group_id == null,
      status: row.status || 'active'
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (isSaving) return;
    setEditorOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showAlert('Required', 'Title is required.', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        short_title: form.short_title.trim() || null,
        description: form.description.trim() || null,
        type: form.type,
        parents: form.parents,
        pathways: form.pathways,
        is_global: form.is_global,
        organization_group_id: form.is_global ? null : undefined,
        status: form.status
      };
      const url = editingId ? `/api/taxonomy/${editingId}` : '/api/taxonomy';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      setCornerNotice({
        isOpen: true,
        title: editingId ? 'Updated' : 'Created',
        text: `"${form.title.trim()}" saved.`,
        position: 'bottom-left',
        autoClose: true,
        viewTime: 4000
      });
      closeEditor();
      await loadRows();
    } catch (err) {
      showAlert('Save failed', err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetchWithAuth(`/api/taxonomy/${deleteConfirm.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Delete failed');
      setDeleteConfirm(null);
      setCornerNotice({
        isOpen: true,
        title: 'Deleted',
        text: `"${deleteConfirm.title}" removed.`,
        position: 'bottom-left',
        autoClose: true,
        viewTime: 4000
      });
      await loadRows();
    } catch (err) {
      setDeleteConfirm(null);
      showAlert('Delete failed', err.message, 'error');
    }
  };

  return (
    <div className="taxonomy-page">
      <LoadingOverlay isVisible={isLoading && !editorOpen} message="Loading taxonomy…" />

      <header className="taxonomy-top">
        <h1 className="taxonomy-title">Taxonomy</h1>
        <p className="taxonomy-subtitle">
          Shared catalog entries (no organization) plus your organization&apos;s own terms.
          Parents are hierarchy; pathways are concentration facets.
        </p>
      </header>

      <div className="taxonomy-toolbar">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Filter by type">
          <option value="">All types</option>
          {TYPE_OPTIONS.map((k) => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search title, short title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search taxonomy"
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: '#475569' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
        <button type="button" className="taxonomy-add-btn" onClick={openCreate}>
          + New entry
        </button>
      </div>

      <div className="taxonomy-table-wrap">
        {filteredRows.length === 0 && !isLoading ? (
          <div className="taxonomy-empty">No taxonomy entries yet. Create subjects like Mathematics or ELA.</div>
        ) : (
          <table className="taxonomy-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Short title</th>
                <th>Type</th>
                <th>Scope</th>
                <th>Description</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>{displayTitle(row)}</td>
                  <td><span className="taxonomy-code">{row.short_title || '—'}</span></td>
                  <td>{row.type}</td>
                  <td>{row.organization_group_id == null ? 'Shared' : 'Organization'}</td>
                  <td><span className="taxonomy-desc" title={row.description || ''}>{row.description || '—'}</span></td>
                  <td>
                    <span className={`taxonomy-badge taxonomy-badge--${row.status === 'active' ? 'active' : 'inactive'}`}>
                      {row.status || 'active'}
                    </span>
                  </td>
                  <td>
                    <div className="taxonomy-row-actions">
                      <button type="button" className="taxonomy-icon-btn" onClick={() => openEdit(row)} title="Edit">
                        Edit
                      </button>
                      <button
                        type="button"
                        className="taxonomy-icon-btn taxonomy-icon-btn--danger"
                        onClick={() => setDeleteConfirm({ id: row.id, title: displayTitle(row) })}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editorOpen ? (
        <div
          className="taxonomy-editor-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="taxonomy-editor-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSaving) closeEditor();
          }}
        >
          <div className="taxonomy-editor-panel">
            <h2 id="taxonomy-editor-title" className="taxonomy-title" style={{ fontSize: '1.125rem' }}>
              {editingId ? 'Edit taxonomy entry' : 'New taxonomy entry'}
            </h2>
            <div className="taxonomy-modal-fields">
              <div className="taxonomy-field">
                <label htmlFor="tax-title">Title *</label>
                <input
                  id="tax-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Mathematics"
                />
              </div>
              <div className="taxonomy-field">
                <label htmlFor="tax-short-title">Short title</label>
                <input
                  id="tax-short-title"
                  value={form.short_title}
                  onChange={(e) => setForm((f) => ({ ...f, short_title: e.target.value }))}
                  placeholder="e.g. Math"
                />
                <p className="taxonomy-field-hint">Optional short label. Left blank stays null.</p>
              </div>
              <div className="taxonomy-field">
                <label htmlFor="tax-type">Type</label>
                <select
                  id="tax-type"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                >
                  {TYPE_OPTIONS.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </div>
              <div className="taxonomy-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={form.is_global}
                    onChange={(e) => {
                      const is_global = e.target.checked;
                      setForm((f) => ({
                        ...f,
                        is_global,
                        parents: is_global
                          ? f.parents.filter((id) => {
                              const row = rows.find((r) => String(r.id) === String(id));
                              return row && row.organization_group_id == null;
                            })
                          : f.parents,
                        pathways: is_global
                          ? f.pathways.filter((id) => {
                              const row = rows.find((r) => String(r.id) === String(id));
                              return row && row.organization_group_id == null;
                            })
                          : f.pathways
                      }));
                    }}
                  />
                  Shared across all organizations
                </label>
                <p className="taxonomy-field-hint">
                  When shared, any organization can use this entry. Parents/pathways must also be shared.
                </p>
              </div>
              <MultiIdSelect
                id="tax-parents"
                label="Parents (optional)"
                options={parentOptions}
                value={form.parents}
                onChange={(parents) => setForm((f) => ({ ...f, parents }))}
                hint="Hierarchy parents (DAG). Hold Ctrl/Cmd to multi-select."
              />
              <MultiIdSelect
                id="tax-pathways"
                label="Pathways (optional)"
                options={pathwayOptions}
                value={form.pathways}
                onChange={(pathways) => setForm((f) => ({ ...f, pathways }))}
                hint="Pathway / concentration facet. Prefer type=concentration rows."
              />
              <div className="taxonomy-field">
                <label htmlFor="tax-desc">Description</label>
                <textarea
                  id="tax-desc"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
              <div className="taxonomy-field">
                <label htmlFor="tax-status">Status</label>
                <select
                  id="tax-status"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="taxonomy-icon-btn" onClick={closeEditor} disabled={isSaving}>
                Cancel
              </button>
              <button type="button" className="taxonomy-add-btn" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteConfirm ? (
        <AlertModal
          isOpen
          onClose={() => setDeleteConfirm(null)}
          variant="warning"
          subject="Delete taxonomy entry?"
          body={`"${deleteConfirm.title}" will be permanently removed. Forms using it as primary subject may lose the link.`}
          showCloseButton={false}
          actions={[
            { label: 'Cancel', onClick: () => setDeleteConfirm(null), variant: 'secondary' },
            { label: 'Delete', onClick: handleDelete, variant: 'primary', style: { background: '#dc2626' } }
          ]}
        />
      ) : null}

      {alertModal ? (
        <AlertModal isOpen onClose={() => setAlertModal(null)} showCloseButton={false} {...alertModal} />
      ) : null}

      {cornerNotice ? (
        <CornerNotice {...cornerNotice} onClose={() => setCornerNotice(null)} />
      ) : null}
    </div>
  );
};

export default TaxonomyManager;
